/**
 * valueTracing.ts
 * Maps a value seen on the rendered preview back to the Excel cell it came from.
 *
 * The DOCX render pipeline loses the link between a printed string and its
 * source cell, so tracing works by matching the clicked text against the values
 * of the group being previewed.
 */

import { ROW_INDEX_KEY, ROW_SHEET_KEY, isRowMetaKey } from "@/types/excel";

export interface ValueSource {
  /** Sheet the value was read from, when the row carries provenance. */
  sheet?: string;
  /** 1-based Excel row number. */
  rowIndex?: number;
  column: string;
  value: string;
}

function normalize(text: string): string {
  return text.replace(/\s+/g, " ").trim().toLowerCase();
}

/**
 * Find the cell that produced a piece of text on the preview.
 * Returns null for labels, totals and anything else not read from a cell.
 */
export function findValueSource(
  rows: Record<string, unknown>[],
  text: string
): ValueSource | null {
  const target = normalize(text);
  if (!target) return null;

  for (const row of rows) {
    for (const [column, raw] of Object.entries(row)) {
      if (isRowMetaKey(column)) continue;
      if (raw == null) continue;
      const value = String(raw);
      if (normalize(value) !== target) continue;
      return {
        sheet: typeof row[ROW_SHEET_KEY] === "string" ? (row[ROW_SHEET_KEY] as string) : undefined,
        rowIndex: typeof row[ROW_INDEX_KEY] === "number" ? (row[ROW_INDEX_KEY] as number) : undefined,
        column,
        value,
      };
    }
  }
  return null;
}

/** One-line description shown next to the preview. */
export function describeValueSource(source: ValueSource, joinKeyValue?: string): string {
  const parts = [
    source.sheet ? `Sheet: ${source.sheet}` : null,
    source.rowIndex ? `Dòng: ${source.rowIndex}` : null,
    `Cột: ${source.column}`,
    joinKeyValue ? `Khóa nối: ${joinKeyValue}` : null,
  ].filter(Boolean);
  return parts.join(" · ");
}
